// ═══════════════════════════════════════════════════════════════════
// Saved Calendar Views V1 — Day 9
// Frontend-only, localStorage-backed persistence for scheduler presets.
// Additive; no backend contract, no RBAC impact. Stores only UI state.
// ═══════════════════════════════════════════════════════════════════

const STORAGE_PREFIX = 'getcare360:calendar:savedViews:v1';
const CURRENT_VERSION = 1;

// Per-user scoping so presets from one login don't leak to another on the same browser.
function storageKey(userId) {
  const scope = userId ? String(userId) : 'anon';
  return `${STORAGE_PREFIX}:${scope}`;
}

function safeParse(raw) {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    if (obj.version !== CURRENT_VERSION) return null;
    if (!Array.isArray(obj.views)) return null;
    return obj;
  } catch {
    return null;
  }
}

function emptyStore() {
  return { version: CURRENT_VERSION, views: [] };
}

export function loadSavedViews(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const parsed = safeParse(raw);
    if (!parsed) return emptyStore();
    // Sanitise each view: drop malformed entries but keep the rest
    const views = parsed.views.filter(v =>
      v && typeof v === 'object'
      && typeof v.id === 'string'
      && typeof v.name === 'string'
      && v.state && typeof v.state === 'object'
    );
    return { version: CURRENT_VERSION, views };
  } catch {
    return emptyStore();
  }
}

function writeStore(userId, store) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(store));
    return true;
  } catch {
    return false;
  }
}

function genId() {
  return `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// State picked from the page — ONLY UI state, never sensitive or transient.
const STATE_KEYS = [
  'viewMode', 'groupBy',
  'statusFilter', 'staffFilter', 'participantFilter', 'serviceFilter',
  'dateAnchor',
];

export function sanitizeState(state) {
  const out = {};
  STATE_KEYS.forEach(k => {
    if (state && state[k] !== undefined && state[k] !== null) out[k] = state[k];
  });
  return out;
}

export function saveView(userId, { name, state, setAsDefault = false }) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { ok: false, error: 'Name is required' };
  const store = loadSavedViews(userId);
  // Duplicate name → overwrite the existing one (explicit, simple behaviour)
  const existing = store.views.find(v => v.name.toLowerCase() === trimmed.toLowerCase());
  const nowIso = new Date().toISOString();
  if (existing) {
    existing.state = sanitizeState(state);
    existing.updatedAt = nowIso;
    if (setAsDefault) {
      store.views.forEach(v => { v.isDefault = false; });
      existing.isDefault = true;
    }
  } else {
    const view = {
      id: genId(),
      name: trimmed,
      isDefault: !!setAsDefault,
      state: sanitizeState(state),
      createdAt: nowIso,
      updatedAt: nowIso,
    };
    if (setAsDefault) {
      store.views.forEach(v => { v.isDefault = false; });
    }
    store.views.push(view);
  }
  const ok = writeStore(userId, store);
  return { ok, store, view: existing || store.views[store.views.length - 1] };
}

export function deleteView(userId, id) {
  const store = loadSavedViews(userId);
  const next = store.views.filter(v => v.id !== id);
  store.views = next;
  const ok = writeStore(userId, store);
  return { ok, store };
}

export function renameView(userId, id, newName) {
  const trimmed = (newName || '').trim();
  if (!trimmed) return { ok: false, error: 'Name is required' };
  const store = loadSavedViews(userId);
  // Prevent duplicate name collision with a DIFFERENT view
  const clash = store.views.find(v => v.id !== id && v.name.toLowerCase() === trimmed.toLowerCase());
  if (clash) return { ok: false, error: 'A view with this name already exists' };
  const view = store.views.find(v => v.id === id);
  if (!view) return { ok: false, error: 'View not found' };
  view.name = trimmed;
  view.updatedAt = new Date().toISOString();
  const ok = writeStore(userId, store);
  return { ok, store };
}

export function setDefaultView(userId, id) {
  const store = loadSavedViews(userId);
  let found = false;
  store.views.forEach(v => {
    const makeDefault = v.id === id;
    if (makeDefault) found = true;
    v.isDefault = makeDefault;
  });
  if (!found) return { ok: false, error: 'View not found' };
  const ok = writeStore(userId, store);
  return { ok, store };
}

export function clearDefaultView(userId) {
  const store = loadSavedViews(userId);
  store.views.forEach(v => { v.isDefault = false; });
  const ok = writeStore(userId, store);
  return { ok, store };
}

export function getDefaultView(userId) {
  const store = loadSavedViews(userId);
  return store.views.find(v => v.isDefault) || null;
}
